import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { generateCarbonReport } from '../../../service/generateCarbonReport';
import { sendCarbonReportEmail } from '@/service/emailService';
import html2pdf from 'html2pdf.js';

interface CarbonCalculatorCTAProps {
  state: {
    projectDurationMonths: number;
    softwareType: string;
    expectedUsers: number;
    avgUsageHoursPerWeek: number;
    userRegion: string;
    devRegion: string;
    infraRegion: string;
    projectLifespanYears: number;
  };
  results: {
    totalEnergy_kWh: number;
    totalCO2_kg: number;
    flightsParisToNY: number;
    breakdown: {
      devEnergy_kWh: number;
      devCO2_kg: number;
      infraEnergy_kWh: number;
      infraCO2_kg: number;
      userEnergy_kWh: number;
      userCO2_kg: number;
    };
  };
}

const CarbonCalculatorCTA: React.FC<CarbonCalculatorCTAProps> = ({ state, results }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Generate PDF
      const element = document.createElement('div');
      element.innerHTML = generateCarbonReport(state, results);
      document.body.appendChild(element);

      const opt = {
        margin: 0,
        filename: "Rapport d'empreinte carbone Orcsys.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: { 
          unit: "cm", 
          format: "a4", 
          orientation: "portrait",
          compress: true
        }
      };

      const pdfBlob = await html2pdf()
        .set(opt)
        .from(element)
        .output('blob');
      
      const pdfFile = new File([pdfBlob], 'Rapport d\'empreinte carbone Orcsys.pdf', { type: 'application/pdf' });

      // Clean up
      document.body.removeChild(element);

      // Send email with report
      await sendCarbonReportEmail({
        name,
        email,
        phone,
        file: pdfFile
      });
      
      setIsSuccess(true);
      setEmail('');
      setName('');
      setPhone('');
      setConsent(false);
      setMarketingConsent(false);
    } catch (err) {
      console.error('Error generating or sending report:', err);
      setError('Une erreur est survenue lors de l\'envoi du rapport. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-xl shadow-lg border border-green-100 max-w-xl mx-auto"
    >
      <div className="flex flex-col gap-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Recevez votre rapport détaillé
          </h3>
          <p className="text-base text-slate-600">
            Téléchargez un PDF complet de votre calcul d'empreinte carbone
          </p>
        </div>
        
        {isSuccess ? (
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
            <p className="text-green-700 font-medium">Votre rapport a été généré avec succès !</p>
            <p className="text-sm text-green-600 mt-1">Vous allez recevoir un email avec le rapport.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom"
                required
                className="w-full px-5 py-3 rounded-lg border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-base"
              />
            </div>
            
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre email"
                required
                className="w-full px-5 py-3 rounded-lg border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-base"
              />
            </div>
            
            {consent && (
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Votre numéro de téléphone (optionnel)"
                  className="w-full px-5 py-3 rounded-lg border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 outline-none transition-all text-base"
                />
              </div>
            )}
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="consent" className="text-sm text-slate-600 leading-tight">
                  J'accepte qu'Orcsys me contacte pour discuter de mon projet et des solutions de compensation carbone
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="marketingConsent"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="marketingConsent" className="text-sm text-slate-600 leading-tight">
                  J'accepte de recevoir des communications commerciales concernant les projets de type {state.softwareType.toLowerCase()}
                </label>
              </div>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-3 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-base"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Génération en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Recevoir le PDF
                </>
              )}
            </button>

            {error && (
              <div className="text-red-600 text-sm text-center mt-2">
                {error}
              </div>
            )}
          </form>
        )}
      </div>
    </motion.div>
  );
};

export default CarbonCalculatorCTA; 